import React from 'react'
import { message, Tag, Tooltip } from 'antd'
import { ArrowDownOutlined, FileTextOutlined, ProfileOutlined } from '@ant-design/icons'
import moment from 'moment'

const operTypeColors: any = {
  Relocating: 'purple',
  Picking: 'blue',
  Returning: 'orange',
  Inbound: 'cyan',
  Production: 'green',
  ManualDelete: 'red',
  ManualAdd: 'gold',
}

// From / To 值解析：识别 "Key:Value" 格式（如 "Customer:Your local coffee co"）
const parseFromToValue = (value?: string) => {
  if (!value) return null
  const idx = value.indexOf(':')
  if (idx > 0 && idx <= 20) {
    return { label: value.slice(0, idx), text: value.slice(idx + 1) || '-' }
  }
  return { label: null, text: value }
}

// 将 { id: item } 映射转为 material-table 列筛选 lookup（{ id: label }）
const toFilterLookup = (lookup: any, getLabel: (item: any) => string | undefined) => {
  const result: any = {}
  Object.keys(lookup || {}).forEach((id: string) => {
    const label = getLabel(lookup[id])
    if (label) result[id] = label
  })
  return result
}

const FromToCell = (props: { fromValue?: string, toValue?: string }) => {
  const from = parseFromToValue(props.fromValue)
  const to = parseFromToValue(props.toValue)
  if (!from && !to) return <span>-</span>

  const renderLine = (prefix: string, parsed: any, color: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color, flexShrink: 0 }}>{prefix}</span>
      {parsed.label && (
        <span
          style={{
            fontSize: 11,
            color: '#8c8c8c',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 3,
            padding: '0 4px',
            flexShrink: 0,
          }}
        >
          {parsed.label}
        </span>
      )}
      <span
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={parsed.text}
      >
        {parsed.text}
      </span>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {from && renderLine('From', from, '#fa8c16')}
      {from && to && (
        <ArrowDownOutlined style={{ fontSize: 10, color: '#bfbfbf', marginLeft: 14 }} />
      )}
      {to && renderLine('To', to, '#52c41a')}
    </div>
  )
}

// Operation Log 查询页列模型
// employeeLookup: { employeeId: { firstName, fullName } }
// productLookup: { productId: product }
const OperationLogColumnModel = (employeeLookup?: any, productLookup?: any) => [
  {
    title: 'Time',
    field: 'createdAt',
    defaultSort: 'desc',
    width: 150,
    render: (rowData: any) => rowData.createdAt
      ? moment.utc(rowData.createdAt).local().format('DD/MM/YY HH:mm')
      : '-',
  },
  {
    title: 'Oper Type',
    field: 'operType',
    width: 110,
    // 列头筛选下拉（客户端过滤当前页数据）
    lookup: {
      Relocating: 'Relocating',
      Picking: 'Picking',
      Returning: 'Returning',
      Inbound: 'Inbound',
      Production: 'Production',
      ManualDelete: 'ManualDelete',
      ManualAdd: 'ManualAdd',
    },
    render: (rowData: any) => rowData.operType
      ? <Tag color={operTypeColors[rowData.operType] || 'default'}>{rowData.operType}</Tag>
      : '-',
  },
  {
    title: 'Product',
    field: 'productId',
    width: 130,
    // 列头筛选下拉（客户端过滤当前页数据）
    lookup: toFilterLookup(productLookup, (product: any) => product?.productCode || String(product?.productId)),
    render: (rowData: any) => {
      const product = productLookup && productLookup[rowData.productId]
      if (!product) return rowData.productId || '-'
      return (
        <Tooltip title={product.productName || product.productId}>
          <span>{product.productCode || product.productId}</span>
        </Tooltip>
      )
    },
  },
  {
    title: 'Operator',
    field: 'employeeId',
    width: 130,
    // 列头筛选下拉（客户端过滤当前页数据）
    lookup: toFilterLookup(employeeLookup, (employee: any) => employee?.fullName),
    render: (rowData: any) => {
      const employee = employeeLookup && employeeLookup[rowData.employeeId]
      if (!employee) return rowData.employeeId || '-'
      return (
        <Tooltip title={employee.fullName}>
          <span>{employee.firstName}</span>
        </Tooltip>
      )
    },
  },
  {
    title: 'From → To',
    field: 'fromValue',
    width: 220,
    sorting: false,
    render: (rowData: any) => (
      <FromToCell fromValue={rowData.fromValue} toValue={rowData.toValue} />
    ),
  },
  {
    title: 'Quantity',
    field: 'quantity',
    // 客户端模糊匹配该行所有 box 的 barCode（来自 boxes[].barCode，藏在嵌套数组里）
    customFilterAndSearch: (filterValue: string, rowData: any) => {
      const boxes: any[] = rowData?.boxes || []
      const needle = String(filterValue || '').toLowerCase()
      if (!needle) return true
      return boxes.some((b: any) => String(b?.barCode ?? '').toLowerCase().includes(needle))
    },
    width: 100,
    sorting: false,
    render: (rowData: any) => {
      const boxes = rowData.boxes || []
      if (!boxes.length) return rowData.quantity ?? '-'
      return (
        <Tooltip
          title={
            <div style={{ whiteSpace: 'nowrap' }}>
              {boxes.map((box: any) => (
                <div key={box.operationLogBoxId}>
                  BarCode: {box.barCode || '-'} &nbsp;|&nbsp; Qty: {box.quantity ?? '-'}
                </div>
              ))}
            </div>
          }
        >
          <span style={{ cursor: 'pointer', borderBottom: '1px dotted #999' }}>{rowData.quantity}</span>
        </Tooltip>
      )
    },
  },
  {
    title: 'Packing Slip / Order',
    field: 'packingSlipNo',
    width: 160,
    sorting: false,
    render: (rowData: any) => {
      const copyText = async (text: string, label: string) => {
        try {
          await navigator.clipboard.writeText(text)
          message.success(`${label} copied`)
        } catch {
          message.error('Copy failed')
        }
      }
      if (!rowData.packingSlipNo && !rowData.orderNo) return <span>-</span>
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
          {rowData.packingSlipNo && (
            <Tooltip title="Click to copy">
              <Tag
                color="blue"
                style={{ cursor: 'pointer', marginInlineEnd: 0 }}
                onClick={() => copyText(rowData.packingSlipNo, 'Packing Slip')}
              >
                <FileTextOutlined /> PS: {rowData.packingSlipNo}
              </Tag>
            </Tooltip>
          )}
          {rowData.orderNo && (
            <Tooltip title="Click to copy">
              <Tag
                color="purple"
                style={{ cursor: 'pointer', marginInlineEnd: 0 }}
                onClick={() => copyText(rowData.orderNo, 'Order No')}
              >
                <ProfileOutlined /> Order: {rowData.orderNo}
              </Tag>
            </Tooltip>
          )}
        </div>
      )
    },
  },
]

export default OperationLogColumnModel
